import {useContext, useEffect, useRef, useState} from "react";
import {useMutation} from "@apollo/client";
import {Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Spinner, Stack, Text} from "@chakra-ui/react";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {acceptInvitationMutation} from "../components/area/areaQueries";
import {AuthContext, AuthContextI} from "../auth/FirebaseAuth";

type AcceptResponse = {
    acceptInvitation?: {
        message?: string;
    };
};

const EMAIL_MISMATCH_MESSAGE = "Invitation link is not for the current email.";

const InvitePage = () => {
    const authContext: AuthContextI = useContext<AuthContextI>(AuthContext);
    const currentEmail = authContext.getAuthUser()?.email || undefined;
    const [searchParams] = useSearchParams();
    const token = (searchParams.get("token") || "").trim();
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [message, setMessage] = useState<string>("");
    const hasAttemptedRef = useRef(false);
    const [attempt, setAttempt] = useState(0);
    const [acceptInvitation] = useMutation<AcceptResponse>(acceptInvitationMutation);

    useEffect(() => {
        if (hasAttemptedRef.current) {
            return;
        }
        hasAttemptedRef.current = true;

        if (!token) {
            setStatus("error");
            setMessage("Invitation link is missing token.");
            return;
        }

        acceptInvitation({
            variables: {
                token
            }
        }).then((response) => {
            setStatus("success");
            setMessage(response.data?.acceptInvitation?.message || "Invitation accepted.");
        }).catch((error: any) => {
            console.error(error);
            setStatus("error");
            setMessage(error?.message || "Unable to accept invitation.");
        });
    }, [acceptInvitation, token, attempt]);

    return (
        <Box minHeight={"100vh"} display={"grid"} placeItems={"center"} padding={4}>
            <Box width={"100%"} maxWidth={"560px"} borderWidth={"1px"} borderRadius={"md"} padding={6} bg={"white"}>
                <Stack spacing={4}>
                    <Text fontSize={"xl"} fontWeight={"bold"}>Organization Invitation</Text>

                    {status === "loading" ? (
                        <Stack direction={"row"} align={"center"} spacing={3}>
                            <Spinner size={"sm"} color={"teal.500"}/>
                            <Text>Accepting invitation...</Text>
                        </Stack>
                    ) : null}

                    {status === "success" ? (
                        <Alert status={"success"} borderRadius={"md"}>
                            <AlertIcon/>
                            <Box>
                                <AlertTitle>Invitation accepted</AlertTitle>
                                <AlertDescription>{message}</AlertDescription>
                            </Box>
                        </Alert>
                    ) : null}

                    {status === "error" ? (
                        <Alert status={"error"} borderRadius={"md"}>
                            <AlertIcon/>
                            <Box>
                                <AlertTitle>Invitation failed</AlertTitle>
                                <AlertDescription>{message}</AlertDescription>
                            </Box>
                        </Alert>
                    ) : null}

                    {status === "error" && message === EMAIL_MISMATCH_MESSAGE && currentEmail ? (
                        <Text fontSize={"sm"} color={"gray.700"}>
                            Currently logged in as <strong>{currentEmail}</strong>.
                        </Text>
                    ) : null}

                    <Stack direction={"row"} spacing={3}>
                        <Button as={RouterLink} to={"/app"} colorScheme={"teal"}>
                            Open app
                        </Button>
                        {status === "error" && token ? (
                            <Button
                                variant={"outline"}
                                onClick={() => {
                                    hasAttemptedRef.current = false;
                                    setStatus("loading");
                                    setMessage("");
                                    setAttempt((prev) => prev + 1);
                                }}
                            >
                                Retry
                            </Button>
                        ) : null}
                        {status === "error" && message === EMAIL_MISMATCH_MESSAGE ? (
                            <Button
                                variant={"outline"}
                                onClick={() => authContext.signOut()}
                            >
                                Log out
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default InvitePage;
